// セーブ・ロードのスロット（IndexedDB と画面）（2026-10-21 に app.js から分けた。2026-10-30 に 9 → 100、9 つずつのページで見せる）
import { S } from './state.js';
import { C } from './core/model.js';
import { DIR, NAME_ON, NS, SERVICE, conf } from './config.js';
import { LANG, txt, txtHtml } from './text.js';
import { catByKey } from './store.js';
import { hasLayers, partsList } from './core/data.js';
import { readStored, saveObject } from './core/savedata.js';
import { unzip, zip } from './core/zlib.js';
import { choosePart, presetName, thumbTransform, thumbnail } from './parts.js';
import { SAVE_KEY } from './storage.js';
import { loadCharacter, readEmbedded } from './importimage.js';
import { closeModal, openModalNode } from './dialog.js';

// スロット1つ（IndexedDB に入れるもの。本物の slots.js は name を [名前, 色]、この画面は名前だけを、どちらも encodeURIComponent で入れる）
export interface SlotValue { maker: string; text: string; thumb: string; name: string | string[]; time: number; label?: string }
// スロット（IndexedDB）。100 まで（2026-10-30、要望で 9 から。キーは前と同じ「保存キー:番号」なので、前の 1〜9 はそのまま）。
// 画面は 9 つずつのページ（SLOT_PAGE）。前に開いたページはメーカーごとに覚える（SLOT_PAGE_KEY + 保存キー。slots.js と同じ）
export const SLOT_COUNT = 100, SLOT_PAGE = 9, SLOT_DB = 'charatSaveSlots', SLOT_STORE = 'slots', SLOT_TAB_KEY = 'charatSlotsTab', SLOT_PAGE_KEY = 'charatSlotsPage:';
export const SLOT_PAGES = Math.ceil(SLOT_COUNT / SLOT_PAGE);
const PRESET_THUMBS = conf('presetThumbs', {});
// サムネイルに入れないレイヤー（背景・台座・キャラ。slots.js と同じ）。背景は bg・bg1・bg2 など、前後に分かれたレイヤーは _r・_f が付く
// （前は名前そのままの bg・ground・chara だけで、Rouge・Blanc の丸い背景（bg1_r・bg1_f）・DRESSUP2 の額（bg2_r・bg2_f）がサムネイルに残っていた。2026-10-28）
export const SLOT_SKIP = /^lcart_(bg\d*|ground|chara)(_[a-z])?$/;
export function slotDb(): Promise<IDBDatabase> {
  if (S.slotDbPromise) return S.slotDbPromise;
  S.slotDbPromise = new Promise((resolve, reject) => {
    if (!window.indexedDB) { reject(new Error('indexedDB')); return; }
    const req = indexedDB.open(SLOT_DB, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(SLOT_STORE); };
    req.onsuccess = () => { resolve(req.result); };
    req.onerror = () => { reject(req.error); };
  });
  S.slotDbPromise.catch(() => { S.slotDbPromise = null; });
  return S.slotDbPromise;
}
export function slotRequest<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  return slotDb().then((conn) => {
    return new Promise((resolve, reject) => {
      const tx = conn.transaction(SLOT_STORE, mode);
      const req = run(tx.objectStore(SLOT_STORE));
      tx.oncomplete = () => { resolve(req ? req.result : undefined); };
      tx.onerror = tx.onabort = () => { reject(tx.error); };
    });
  });
}
export function slotKey(n: number) { return SERVICE + ':' + n; }
// from〜to 番のスロット（1つのトランザクションで）
export function readSlots(from = 1, to = SLOT_COUNT): Promise<(SlotValue | undefined)[]> {
  return slotDb().then((conn) => new Promise((resolve, reject) => {
    const tx = conn.transaction(SLOT_STORE, 'readonly'), store = tx.objectStore(SLOT_STORE), reqs: IDBRequest<SlotValue>[] = [];
    for (let n = from; n <= to; n++) reqs.push(store.get(slotKey(n)));
    tx.oncomplete = () => { resolve(reqs.map((r) => r.result)); };
    tx.onerror = tx.onabort = () => { reject(tx.error); };
  }));
}
// 中身の入っているスロットの番号（キーだけ読む。ページの印・書き出し・「まだ無い」の判断に）
export function filledSlots(): Promise<number[]> {
  const pick = (keys: IDBValidKey[]) => keys.map((k) => Number(String(k).slice(SERVICE.length + 1))).filter((n) => n >= 1 && n <= SLOT_COUNT && n % 1 === 0).sort((a, b) => a - b);
  return slotDb().then((conn) => {
    const store = conn.transaction(SLOT_STORE, 'readonly').objectStore(SLOT_STORE);
    if (!store.getAllKeys) return readSlots().then((list) => list.map((v, i) => (v && v.text ? i + 1 : 0)).filter((n) => n > 0));
    return new Promise<number[]>((resolve, reject) => {
      const req = store.getAllKeys(IDBKeyRange.bound(SERVICE + ':', SERVICE + ':\uffff'));
      req.onsuccess = () => { resolve(pick(req.result)); };
      req.onerror = () => { reject(req.error); };
    });
  });
}
// セーブしたら、ブラウザに「消さないで」と頼む（容量が足りないときなどに勝手に消されにくくなる。Chrome・Safari は聞かずに決める、Firefox は1度だけたずねる）
export function keepSlots() {
  try {
    const st = navigator.storage;
    if (st && st.persist && st.persisted) st.persisted().then((done) => { if (!done) return st.persist(); }).catch(() => {});
  } catch (e) {}
}
export function writeSlot(n: number, value: SlotValue) { return slotRequest('readwrite', (store) => store.put(value, slotKey(n))); }
export function removeSlot(n: number) { return slotRequest('readwrite', (store) => store.delete(slotKey(n))); }
// サムネイル: 今のキャラから背景・台座・キャラ・名前を外し、透明な余白を切り落として長いほうの辺 160px の PNG に（slots.js と同じ）
export function slotThumbnail() {
  const copy = S.stageSvg.cloneNode(true) as SVGSVGElement;
  const name = copy.querySelector('#nameLayer');
  if (name) name.remove();
  copy.querySelectorAll('[id^="lcart_"]').forEach((g) => { if (SLOT_SKIP.test(g.id)) g.textContent = ''; });
  copy.setAttribute('width', 400);
  copy.setAttribute('height', 400);
  let source = new XMLSerializer().serializeToString(copy);
  if (source.indexOf('xmlns=') < 0) source = source.replace('<svg', '<svg xmlns="' + NS + '"');
  return new Promise<string>((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 400;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, 400, 400);
      let d = ctx.getImageData(0, 0, 400, 400).data, top = 400, left = 400, right = -1, bottom = -1;
      for (let y = 0; y < 400; y++) for (var x = 0; x < 400; x++) {
        if (d[(y * 400 + x) * 4 + 3] > 8) { if (y < top) top = y; if (y > bottom) bottom = y; if (x < left) left = x; if (x > right) right = x; }
      }
      if (right < 0) { resolve(''); return; }
      const w = right - left + 1, h = bottom - top + 1, k = Math.min(1, 160 / Math.max(w, h));
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(w * k)); out.height = Math.max(1, Math.round(h * k));
      out.getContext('2d')!.drawImage(canvas, left, top, w, h, 0, 0, out.width, out.height);
      resolve(out.toDataURL('image/png'));
    };
    img.onerror = () => { resolve(''); };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(source);
  });
}
export function slotDate(time: number) {
  const d = new Date(time);
  try { return d.toLocaleString(LANG, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) {}
  return (d.getMonth() + 1) + '/' + d.getDate() + ' ' + d.getHours() + ':' + ('0' + d.getMinutes()).slice(-2);
}
// セーブ・ロードの画面（タブは前に使ったほう。本物の slots.js と同じ charatSlotsTab）
export function openSlots(tab?: string | null) {
  if (!tab) { try { tab = localStorage.getItem(SLOT_TAB_KEY); } catch (e) {} }
  tab = tab === 'load' || tab === 'preset' ? tab : 'save';
  var root = document.createElement('div');
  root.className = 'slots';
  root.innerHTML =
    '<div class="slots-tabs" role="tablist"><button type="button" role="tab" data-tab="save">' + txtHtml('slot_save') + '</button><button type="button" role="tab" data-tab="load">' + txtHtml('slot_load') + '</button>' +
    '<button type="button" role="tab" data-tab="preset">' + txtHtml('preset_tab') + '</button></div>' +
    '<p class="slots-lead"></p><div class="slots-pages" role="group"></div><div class="slots-grid"></div>' +
    '<p class="slots-msg" role="status" aria-live="polite"></p>' +
    '<p class="note">' + txtHtml('slot_note') + '</p>' +
    '<div class="slots-file"><button type="button" class="pill">' + txtHtml('slot_export') + '</button><label class="pill">' + txtHtml('slot_import') +
    '<input type="file" accept=".json,application/json"></label></div>';
  var grid = root.querySelector<HTMLElement>('.slots-grid')!, lead = root.querySelector('.slots-lead')!, msg = root.querySelector('.slots-msg')!, msgTimer = 0;
  var pages = root.querySelector<HTMLElement>('.slots-pages')!, fileRow = root.querySelector<HTMLElement>('.slots-file')!;
  pages.setAttribute('aria-label', txt('slot_pages'));
  // 今のページ（前に開いたページ。メーカーごと）
  var page = 1;
  try { page = Number(localStorage.getItem(SLOT_PAGE_KEY + SERVICE)) || 1; } catch (e) {}
  page = Math.min(SLOT_PAGES, Math.max(1, Math.floor(page)));
  var say = (text: string) => { msg.textContent = text; clearTimeout(msgTimer); msgTimer = setTimeout(() => { msg.textContent = ''; }, 2600); };
  root.querySelectorAll<HTMLElement>('[role="tab"]').forEach((b) => { b.addEventListener('click', () => { show(b.dataset.tab!); }); });
  // ページの列（1〜12。セーブの入っているページには点）
  var drawPages = (filled: number[]) => {
    pages.textContent = '';
    for (let p = 1; p <= SLOT_PAGES; p++) {
      const from = (p - 1) * SLOT_PAGE + 1, to = Math.min(SLOT_COUNT, p * SLOT_PAGE);
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = String(p);
      b.title = txt('slot_page', { from: from, to: to });
      b.setAttribute('aria-label', b.title);
      if (p === page) b.setAttribute('aria-current', 'page');
      if (filled.some((n) => n >= from && n <= to)) b.classList.add('has');
      b.addEventListener('click', () => {
        if (p === page) return;
        page = p;
        try { localStorage.setItem(SLOT_PAGE_KEY + SERVICE, String(p)); } catch (e) {}
        show(tab!);
      });
      pages.appendChild(b);
    }
  };
  // 一覧の上の横長の欄で「やめる」「戻す」などを選ばせる（ファイルから戻すとき）
  var askBar = (text: string, okLabel: string, run: () => void) => {
    closeRename();
    const ask = document.createElement('div');
    ask.className = 'slot-rename slot-bar';
    ask.innerHTML = '<p></p><div><button type="button" class="pill">' + txtHtml('slot_cancel') + '</button><button type="button" class="cta"></button></div>';
    ask.querySelector('p')!.textContent = text;
    ask.querySelector('.cta')!.textContent = okLabel;
    ask.querySelector('.pill')!.addEventListener('click', () => { ask.remove(); });
    ask.querySelector('.cta')!.addEventListener('click', () => { ask.remove(); run(); });
    grid.parentNode!.insertBefore(ask, pages);
    ask.scrollIntoView({ block: 'nearest' });
    ask.querySelector<HTMLElement>('.cta')!.focus();
  };
  // ファイルに書き出す（このメーカーのセーブ全部。ほかのブラウザ・端末へ移す・控えにする）・ファイルから戻す（同じ番号のスロットへ）
  fileRow.querySelector('button')!.addEventListener('click', () => {
    exportSlots(DIR).then((n) => { say(n ? txt('slot_exported', { n: n }) : txt('slot_export_none')); }, () => { say(txt('slot_unavailable')); });
  });
  var fileInput = fileRow.querySelector('input')!;
  fileInput.addEventListener('change', () => {
    const f = fileInput.files && fileInput.files[0];
    fileInput.value = '';
    if (!f) return;
    readSlotsFile(f).then((got) => {
      if (got === 'other') { say(txt('slot_import_other')); return; }
      if (!got || !got.length) { say(txt('slot_import_bad')); return; }
      filledSlots().catch(() => [] as number[]).then((have) => {
        const clash = got.filter((v) => have.indexOf(v.n) >= 0).length;
        askBar(txt('slot_import_confirm', { n: got.length }) + (clash ? ' ' + txt('slot_import_overwrite', { m: clash }) : ''), txt('slot_import_ok'), () => {
          writeSlots(got).then(() => {
            keepSlots();
            // 戻したいちばん小さい番号のページを開く
            page = Math.ceil(got[0].n / SLOT_PAGE);
            try { localStorage.setItem(SLOT_PAGE_KEY + SERVICE, String(page)); } catch (e) {}
            show(tab!);
            say(txt('slot_imported', { n: got.length }));
          }, () => { say(txt('slot_failed')); });
        });
      });
    });
  });
  // カードの上で「やめる」「上書き」などを選ばせる（押しまちがいで消えないように）
  var confirmOn = (card: HTMLElement, text: string, okLabel: string, run: () => void) => {
    const old = card.querySelector('.slot-ask');
    if (old) old.remove();
    const ask = document.createElement('div');
    ask.className = 'slot-ask';
    ask.innerHTML = '<p></p><div><button type="button" class="pill">' + txtHtml('slot_cancel') + '</button><button type="button" class="cta"></button></div>';
    ask.querySelector('p')!.textContent = text;
    ask.querySelector('.cta')!.textContent = okLabel;
    ask.querySelector('.pill')!.addEventListener('click', (e) => { e.stopPropagation(); ask.remove(); });
    ask.querySelector('.cta')!.addEventListener('click', (e) => { e.stopPropagation(); ask.remove(); run(); });
    card.appendChild(ask);
    ask.querySelector<HTMLElement>('.cta')!.focus();
  };
  // テンプレート（データの「キャラ」カテゴリのプリセット。001 の「最後に完成したキャラ」はロードの先頭にあるので出さない）
  function showPresets() {
    grid.textContent = '';
    grid.classList.add('presets');
    const chara = catByKey.chara;
    const list = chara ? partsList('chara').filter((p) => hasLayers(p) && p.id !== 1) : [];
    list.forEach((part) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'slot preset';
      b.dataset.id = String(part.id);
      // サムネイルは保存データから描いたキャラの画像（2026-10-28〜。tools/chibi-unify/preset-thumbs.mjs が作る。全身のメーカーは上半身、CHOCO などは全身）。
      // 画像の無いテンプレートだけ、前のとおりデータの「キャラ」のパーツの絵（メーカーによって全身・顔だけ・番号だけで、寄せ方も決め打ちでずれていた）
      const ver = PRESET_THUMBS[part.id];
      if (ver) {
        const img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        img.src = '/' + DIR + '/images/presets/' + part.id + '.webp?' + ver;
        b.appendChild(img);
      } else {
        const svg = thumbnail(chara, part);
        (svg.firstChild as SVGGElement).setAttribute('transform', thumbTransform(chara, null));
        b.appendChild(svg);
      }
      b.title = presetName(part.id);
      b.setAttribute('aria-label', presetName(part.id));
      b.addEventListener('click', () => { closeModal(); choosePart(chara, part, partsList('chara')); });
      grid.appendChild(b);
    });
  }
  // 名前の欄は一覧の上に横長に（カードの上では小さくて入らない）。名前を付けているカードには枠
  function renameOn(card: HTMLElement, n: number, now: string) {
    closeRename();
    const ask = document.createElement('div');
    ask.className = 'slot-rename';
    ask.innerHTML = '<p></p><input type="text" enterkeyhint="done"><div><button type="button" class="pill">' + txtHtml('slot_cancel') + '</button><button type="button" class="cta">' + txtHtml('slot_rename_ok') + '</button></div>';
    ask.querySelector('p')!.textContent = txt('slot_rename_head', { n: n });
    const input = ask.querySelector('input')!;
    input.maxLength = SLOT_LABEL_MAX;
    input.value = now;
    input.placeholder = txt('slot_label_ph');
    input.setAttribute('aria-label', txt('slot_rename_head', { n: n }));
    const close = closeRename;
    const done = () => {
      const label = input.value.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, SLOT_LABEL_MAX);
      close();
      if (label === now) return;
      renameSlot(n, label).then(() => { say(txt(label ? 'slot_renamed' : 'slot_unnamed', { n: n })); show(tab!); }, () => { say(txt('slot_failed')); });
    };
    ask.querySelector('.pill')!.addEventListener('click', (e) => { e.stopPropagation(); close(); });
    ask.querySelector('.cta')!.addEventListener('click', (e) => { e.stopPropagation(); done(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); done(); }
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    });
    grid.parentNode!.insertBefore(ask, grid);
    card.classList.add('renaming');
    ask.scrollIntoView({ block: 'nearest' });
    input.focus();
    input.select();
  }
  function closeRename() {
    root.querySelectorAll('.slot-rename').forEach((bar) => { bar.remove(); });
    root.querySelectorAll('.slot.renaming').forEach((c) => { c.classList.remove('renaming'); });
  }
  function show(which: string) {
    tab = which;
    closeRename();
    grid.classList.remove('presets');
    try { localStorage.setItem(SLOT_TAB_KEY, tab); } catch (e) {}
    root.querySelectorAll<HTMLElement>('[role="tab"]').forEach((b) => { b.setAttribute('aria-selected', b.dataset.tab === tab ? 'true' : 'false'); });
    lead.textContent = txt(tab === 'save' ? 'slot_save_lead' : tab === 'preset' ? 'preset_lead' : 'slot_load_lead');
    root.querySelector<HTMLElement>('.note')!.hidden = tab === 'preset'; // 「この端末に保存」は、テンプレートには関係ない
    pages.hidden = fileRow.hidden = tab === 'preset';
    if (tab === 'preset') { showPresets(); return; }
    const shown = page, from = (page - 1) * SLOT_PAGE + 1, to = Math.min(SLOT_COUNT, page * SLOT_PAGE);
    Promise.all([readSlots(from, to), filledSlots(), tab === 'load' ? readStored(SAVE_KEY) : null]).then(([slots, filled, last]) => {
      if (tab !== which || page !== shown) return;
      grid.textContent = '';
      drawPages(filled);
      // ロード: 先頭に「最後に完成したキャラ」（パーツ一覧の「キャラ」の 001 と同じ）
      if (last) {
        const lb = document.createElement('button');
        lb.type = 'button';
        lb.className = 'slot slot-last';
        lb.innerHTML = '<span>' + txtHtml('slot_last') + '</span>';
        lb.addEventListener('click', () => { loadCharacter(last, txt('slot_last')); });
        grid.appendChild(lb);
      }
      slots.forEach((slot, i) => {
        const n = from + i, has = !!(slot && slot.text);
        const card = document.createElement('div');
        card.className = 'slot' + (has ? ' filled' : '');
        const main = document.createElement('button');
        main.type = 'button';
        main.className = 'slot-main';
        const title = has ? slotTitle(slot!) : '';
        if (title) card.classList.add('named');
        main.innerHTML = '<span class="slot-no">' + n + '</span>' +
          (has ? (slot!.thumb ? '<img alt="">' : '<span class="slot-empty"></span>') +
            (title ? '<span class="slot-label"></span>' : '') + '<span class="slot-date">' + slotDate(slot!.time) + '</span>'
            : '<span class="slot-empty">' + txtHtml(tab === 'save' ? 'slot_save_here' : 'slot_empty') + '</span>');
        if (has && slot!.thumb) main.querySelector('img')!.src = slot!.thumb; // 文字列を HTML に混ぜない（ファイルから戻したものもあるので）
        if (title) main.querySelector('.slot-label')!.textContent = title;
        main.setAttribute('aria-label', has ? txt('slot_aria', { n: n, date: slotDate(slot!.time) }) + (title ? ' ' + title : '') : txt('slot_aria_empty', { n: n }));
        if (tab === 'load' && !has) main.disabled = true;
        main.addEventListener('click', () => {
          if (tab === 'save') {
            const run = () => { saveToSlot(n, has ? slot!.label : '').then(() => { keepSlots(); say(txt('slot_saved', { n: n })); show('save'); }, () => { say(txt('slot_failed')); }); };
            if (has) confirmOn(card, txt('slot_overwrite', { n: n }), txt('slot_overwrite_ok'), run); else run();
          } else if (has) {
            unzip(slot!.text).then((t) => JSON.parse(t)).catch(() => null).then((save) => {
              if (save) loadCharacter(save, txt('slot_n', { n: n })); else say(txt('slot_load_failed'));
            });
          }
        });
        card.appendChild(main);
        if (has) {
          const del = document.createElement('button');
          del.type = 'button';
          del.className = 'slot-del';
          del.setAttribute('aria-label', txt('slot_delete_aria', { n: n }));
          del.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17"/></svg>';
          del.addEventListener('click', () => {
            confirmOn(card, txt('slot_delete_confirm', { n: n }), txt('slot_delete'), () => {
              removeSlot(n).then(() => { say(txt('slot_deleted', { n: n })); show(tab!); }, () => { say(txt('slot_delete_failed')); });
            });
          });
          card.appendChild(del);
          const ren = document.createElement('button');
          ren.type = 'button';
          ren.className = 'slot-ren';
          ren.title = txt('slot_rename');
          ren.setAttribute('aria-label', txt('slot_rename_aria', { n: n }));
          ren.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z"/><path d="M13.5 6.5l4 4"/></svg>';
          ren.addEventListener('click', () => { renameOn(card, n, slot!.label || ''); });
          card.appendChild(ren);
        }
        grid.appendChild(card);
      });
      if (tab === 'load' && !filled.length && !last) {
        grid.innerHTML = '<p class="slots-none">' + txtHtml('slot_none') + '</p>';
        pages.hidden = true;
      }
      if (tab === 'load') grid.insertBefore(restoreButton(), grid.firstChild);
    }, () => {
      if (tab !== which) return;
      pages.hidden = fileRow.hidden = true;
      grid.innerHTML = '<p class="slots-none">' + txtHtml('slot_unavailable') + '</p>';
      if (tab === 'load') grid.insertBefore(restoreButton(), grid.firstChild);
    });
  }
  // 画像から読み込む（ファイルを選ぶ。PC は画面に落としてもよい）
  function restoreButton() {
    const b = document.createElement('label');
    b.className = 'slot-restore';
    b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="4.5" width="17" height="15" rx="3"/><circle cx="9" cy="10" r="1.8"/><path d="M5 18l5-5 3.5 3.5L16 14l3 3"/></svg>' +
      '<span><b>' + txtHtml('restore_btn') + '</b><small>' + txtHtml('restore_note') + '</small></span>';
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/*';
    input.addEventListener('change', () => { if (input.files![0]) restoreFrom(input.files![0]); input.value = ''; });
    b.appendChild(input);
    return b;
  }
  function restoreFrom(f: File) {
    say(txt('loading'));
    readEmbedded(f).then((save) => {
      if (!save) { say(txt('restore_none')); return; }
      if (save === 'other') { say(txt('restore_other')); return; }
      loadCharacter(save, txt('restore_label'));
    });
  }
  root.addEventListener('dragover', (e) => { if (e.dataTransfer && Array.prototype.indexOf.call(e.dataTransfer.types, 'Files') >= 0) { e.preventDefault(); root.classList.add('drop'); } });
  root.addEventListener('dragleave', (e) => { if (e.target === root) root.classList.remove('drop'); });
  root.addEventListener('drop', (e) => {
    e.preventDefault();
    root.classList.remove('drop');
    const f = e.dataTransfer && e.dataTransfer.files[0];
    if (f) restoreFrom(f);
  });
  // PC は「ファイルに書き出す・戻す」を下の「閉じる」の左に（一覧の下だと、ロードの画面で隠れてスクロールしないと見えない）。スマホは一覧の下のまま
  const wide = window.matchMedia('(min-width: 761px) and (min-aspect-ratio: 4/5)').matches;
  if (wide) fileRow.remove();
  openModalNode(root, wide ? [fileRow] : undefined);
  show(tab);
}
// label: スロットの名前（モックだけ。上書きしても前に付けた名前は残す。本物の slots.js は読まず、本物で上書きすると消える）
export function saveToSlot(n: number, label: string | undefined) {
  const obj = saveObject();
  return Promise.all([slotThumbnail(), zip(JSON.stringify(obj))]).then(([thumb, text]) => {
    const value: SlotValue = { maker: SERVICE, text: text, thumb: thumb, name: NAME_ON ? encodeURIComponent(C.name[0] || '') : '', time: Date.now() };
    if (label) value.label = label;
    return writeSlot(n, value);
  });
}
// スロットに見せる名前: 付けた名前、無ければキャラの名前（本物は [名前, 色]・モックは名前だけを、どちらも encodeURIComponent で入れている）
export function slotTitle(slot: SlotValue) {
  if (slot.label) return String(slot.label);
  if (!NAME_ON) return ''; // 名前の無いメーカー（クラシック版のスロットに昔の台詞が入っていても出さない）
  const name = Array.isArray(slot.name) ? slot.name[0] : slot.name;
  try { return decodeURIComponent(name || ''); } catch (e) { return ''; }
}
export const SLOT_LABEL_MAX = 16;
// ---- ファイルに書き出す・ファイルから戻す（2026-10-30。slots.js と同じ形） ----
// { type: 'charat-slots', v: 1, maker: 保存キー, time, slots: [{ n: 番号, maker, text, thumb, name, time, label? }] }
export const SLOT_FILE_TYPE = 'charat-slots';
// 書き出したキャラの数を返す（0 ならファイルは作らない）。ファイル名は charat-{メーカー}-slots-{年月日}.json
export function exportSlots(dir: string) {
  return readSlots().then((list) => {
    const slots = list.map((v, i) => (v && v.text ? Object.assign({ n: i + 1 }, v) : null)).filter((v) => !!v);
    if (!slots.length) return 0;
    const blob = new Blob([JSON.stringify({ type: SLOT_FILE_TYPE, v: 1, maker: SERVICE, time: Date.now(), slots: slots })], { type: 'application/json' });
    const d = new Date(), pad = (v: number) => (v < 10 ? '0' : '') + v;
    const a = document.createElement('a');
    a.download = 'charat-' + dir.replace(/[^a-z0-9_-]+/gi, '-') + '-slots-' + d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate()) + '.json';
    a.href = URL.createObjectURL(blob);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => { URL.revokeObjectURL(a.href); }, 60000);
    return slots.length;
  });
}
// ファイルの中身を確かめて、戻すスロット（番号の小さい順）を返す。ほかのメーカーのファイルは 'other'、読めなければ null
export function readSlotsFile(file: Blob): Promise<(SlotValue & { n: number })[] | 'other' | null> {
  return new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { resolve(String(r.result || '')); };
    r.onerror = () => { reject(r.error); };
    r.readAsText(file);
  }).then((text) => {
    const data = JSON.parse(text);
    if (!data || data.type !== SLOT_FILE_TYPE || !Array.isArray(data.slots)) return null;
    if (data.maker !== SERVICE) return 'other';
    const out: Record<number, SlotValue & { n: number }> = {};
    data.slots.forEach((v: any) => {
      if (!v || typeof v !== 'object') return;
      const n = Number(v.n);
      if (!(n >= 1 && n <= SLOT_COUNT && n % 1 === 0)) return;
      // 保存データは 1 文字 1 バイトの圧縮した文字列、サムネイルは PNG の data URL だけ
      if (typeof v.text !== 'string' || !v.text || !/^[\u0000-\u00ff]*$/.test(v.text)) return;
      const thumb = typeof v.thumb === 'string' && /^data:image\/png;base64,[A-Za-z0-9+/=]*$/.test(v.thumb) ? v.thumb : '';
      const name = typeof v.name === 'string' ? v.name.slice(0, 400) : Array.isArray(v.name) ? v.name.slice(0, 4).map((x: unknown) => String(x).slice(0, 400)) : '';
      const value: SlotValue & { n: number } = { n: n, maker: SERVICE, text: v.text, thumb: thumb, name: name, time: Number(v.time) > 0 ? Number(v.time) : Date.now() };
      const label = typeof v.label === 'string' ? v.label.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, SLOT_LABEL_MAX) : '';
      if (label) value.label = label;
      out[n] = value;
    });
    return Object.keys(out).map(Number).sort((a, b) => a - b).map((n) => out[n]);
  }).catch(() => null);
}
// まとめて入れる（1つのトランザクション）
export function writeSlots(list: (SlotValue & { n: number })[]) {
  return slotRequest('readwrite', (store) => {
    let req: IDBRequest<IDBValidKey> | null = null;
    list.forEach((v) => {
      const value: SlotValue = { maker: v.maker, text: v.text, thumb: v.thumb, name: v.name, time: v.time };
      if (v.label) value.label = v.label;
      req = store.put(value, slotKey(v.n));
    });
    return req!;
  });
}
export function renameSlot(n: number, label: string) {
  return slotRequest<SlotValue>('readonly', (store) => store.get(slotKey(n))).then((slot) => {
    if (!slot) throw new Error('empty');
    if (label) slot.label = label; else delete slot.label;
    return writeSlot(n, slot);
  });
}
